'use client';

import type {
  HeaderImage,
  HeaderLabels,
  HeaderLink,
  NavCategory,
  NavSubcategory,
} from './header-types';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { HEADER_DRAWER_ID } from './header-config';
import { useHeaderDrawer } from './header-drawer-store';
import { CloseIcon } from './header-icons';

const summaryClassName =
  "flex cursor-pointer list-none items-center gap-2.5 after:ml-auto after:pl-3 after:font-bold after:text-[#00a1e6] after:content-['+'] [&::-webkit-details-marker]:hidden";
const subLinkClassName = 'block py-1.5 text-[.9rem] text-[#5f6b76]';
const FOCUSABLE_SELECTOR = 'a[href], button, summary';

type HeaderDrawerProps = {
  categories: NavCategory[];
  links: HeaderLink[];
  logo: HeaderImage | null;
  labels: Pick<HeaderLabels, 'menu' | 'close' | 'showAll'>;
};

export function HeaderDrawer({ categories, links, logo, labels }: HeaderDrawerProps) {
  const isOpen = useHeaderDrawer(state => state.isOpen);
  const close = useHeaderDrawer(state => state.close);
  const pathname = usePathname();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  // The category tree is mounted on first open; until then the mega menu carries the links.
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    close();
  }, [pathname, close]);

  useEffect(() => {
    if (!isOpen) return;

    setHasOpened(true);
    // Safari does not focus a tapped button, so fall back to the menu button that opened the drawer.
    const previousFocus =
      document.activeElement instanceof HTMLElement && document.activeElement !== document.body
        ? document.activeElement
        : document.querySelector<HTMLElement>(`[aria-controls="${HEADER_DRAWER_ID}"]`);
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key === 'Tab' && panelRef.current) trapFocus(event, panelRef.current);
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [isOpen, close]);

  return (
    <div
      id={HEADER_DRAWER_ID}
      role="dialog"
      aria-modal="true"
      aria-label={labels.menu}
      onClick={event => {
        if (event.target === event.currentTarget) close();
      }}
      className={cn(
        'fixed inset-0 z-[100] bg-[rgba(10,14,18,.5)]',
        // Visibility switches at once when opening, so the close button can take focus right away.
        isOpen
          ? 'visible opacity-100 [transition:opacity_.25s]'
          : 'invisible opacity-0 [transition:opacity_.25s,visibility_0s_.25s]',
      )}
    >
      <div
        ref={panelRef}
        onClick={event => {
          // Same-page, hash and query links keep the pathname, so close on the click itself.
          if (event.target instanceof Element && event.target.closest('a[href]')) close();
        }}
        className={cn(
          'h-full w-[min(380px,88vw)] overflow-y-auto bg-white px-4 pb-8 pt-3 [transition:transform_.3s]',
          isOpen ? '[transform:none]' : '[transform:translateX(-100%)]',
        )}
      >
        <div className="flex items-center justify-between border-b border-[#e6eaee] pb-3.5 pt-1.5">
          {logo && (
            <Image
              src={logo.url}
              alt={logo.alt}
              width={333}
              height={54}
              className="h-[34px] w-auto"
            />
          )}
          <button
            ref={closeButtonRef}
            type="button"
            aria-label={labels.close}
            onClick={close}
            className="flex cursor-pointer bg-transparent text-[#14181c]"
          >
            <CloseIcon />
          </button>
        </div>
        {(isOpen || hasOpened) &&
          categories.map(category => (
            <DrawerCategory key={category.id} category={category} showAllLabel={labels.showAll} />
          ))}
        {links.length > 0 && (
          <div className="flex flex-col gap-3 pt-[18px] font-semibold">
            {links.map((link, index) => (
              <Link key={index} href={link.href} prefetch={false}>
                {link.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Keeps Tab and Shift+Tab inside the open drawer (it is modal). */
function trapFocus(event: KeyboardEvent, panel: HTMLElement) {
  const focusable = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(
    element => element.offsetParent !== null,
  );
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;

  if (!panel.contains(active) || (event.shiftKey ? active === first : active === last)) {
    event.preventDefault();
    (event.shiftKey ? last : first)?.focus();
  }
}

function DrawerCategory({
  category,
  showAllLabel,
}: {
  category: NavCategory;
  showAllLabel: string;
}) {
  const icon = category.iconUrl && (
    <Image src={category.iconUrl} alt="" width={20} height={20} className="flex-none" />
  );

  if (!category.children.length) {
    return (
      <Link
        href={category.href}
        prefetch={false}
        className="flex items-center gap-2.5 border-b border-[#e6eaee] py-3.5 font-bold"
      >
        {icon}
        {category.name}
      </Link>
    );
  }

  return (
    <details className="group/category">
      <summary
        className={cn(
          summaryClassName,
          "border-b border-[#e6eaee] py-3.5 font-bold group-open/category:after:content-['−']",
        )}
      >
        {icon}
        {category.name}
      </summary>
      <div className="pb-2 pl-3 pt-1">
        {category.children.map(subcategory => (
          <DrawerSubcategory
            key={subcategory.id}
            subcategory={subcategory}
            showAllLabel={showAllLabel}
          />
        ))}
        <Link
          href={category.href}
          prefetch={false}
          className="block py-2.5 text-[.93rem] font-semibold text-[#00a1e6]"
        >
          {showAllLabel}: {category.name} <span aria-hidden>→</span>
        </Link>
      </div>
    </details>
  );
}

function DrawerSubcategory({
  subcategory,
  showAllLabel,
}: {
  subcategory: NavSubcategory;
  showAllLabel: string;
}) {
  if (!subcategory.children.length) {
    return (
      <Link
        href={subcategory.href}
        prefetch={false}
        className="block py-2.5 text-[.93rem] font-medium"
      >
        {subcategory.name}
      </Link>
    );
  }

  return (
    <details className="group/subcategory">
      <summary
        className={cn(
          summaryClassName,
          "py-2.5 text-[.93rem] font-medium group-open/subcategory:after:content-['−']",
        )}
      >
        {subcategory.name}
      </summary>
      <ul className="pb-2 pl-3">
        {subcategory.children.map(link => (
          <li key={link.id}>
            <Link href={link.href} prefetch={false} className={subLinkClassName}>
              {link.name}
            </Link>
          </li>
        ))}
        <li>
          <Link
            href={subcategory.href}
            prefetch={false}
            className={cn(subLinkClassName, 'font-semibold text-[#00a1e6]')}
          >
            {showAllLabel} <span aria-hidden>→</span>
          </Link>
        </li>
      </ul>
    </details>
  );
}
